import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import heroVideo from "../assests/video/HERO.mp4";
import { cachedFetchJson } from "../utils/apiCache";

const getVideoId = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith("/shorts/")) {
      return parsed.pathname.split("/shorts/")[1]?.split("/")[0] || "";
    }
    if (parsed.searchParams.get("v")) return parsed.searchParams.get("v");
    const parts = parsed.pathname.split("/");
    return parts[parts.length - 1] || "";
  } catch {
    if (url.includes("shorts/")) return url.split("shorts/")[1]?.split("?")[0] || "";
    if (url.includes("watch?v=")) return url.split("watch?v=")[1]?.split("&")[0] || "";
    return "";
  }
};

const EMBED_HOST = "https://www.youtube.com";

const Herosection = () => {
  const navigate = useNavigate();
  const heroIframeRef = useRef(null);
  const [banners, setBanners] = useState([]);
  const [activeLaptop, setActiveLaptop] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [bannerError, setBannerError] = useState(null);
  const [stats, setStats] = useState({ experience: 0, brands: 0, customers: 0 });
  const [reels, setReels] = useState([]);
  const [heroPlaying, setHeroPlaying] = useState(false);
  const [testimonialImages, setTestimonialImages] = useState([]);
  const [testimonialCount, setTestimonialCount] = useState(0);
  const embedOrigin = typeof window !== "undefined" ? window.location.origin : "";

  const forceHeroPlay = () => {
    const frame = heroIframeRef.current;
    if (!frame || !frame.contentWindow) return;
    frame.contentWindow.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*");
    frame.contentWindow.postMessage(JSON.stringify({ event: "command", func: "mute", args: [] }), "*");
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setBanners(prev => {
        if (prev.length === 0) return prev;
        setActiveLaptop(a => (a + 1) % prev.length);
        return prev;
      });
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      try {
        setIsLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const backendRoot = process.env.REACT_APP_BACKEND;
        const baseUrl = (backendUrl || "http://localhost:8000").replace(/\/$/, "");
        
        const bRes = await cachedFetchJson(`${baseUrl}/banner`, { ttlMs: 120000 });
        const bData = Array.isArray(bRes) ? bRes : (bRes?.data || []);
        const assetBase = (process.env.REACT_APP_ASSET_BASE_URL || backendRoot || baseUrl).replace(/\/$/, "");
        const normalizedBanners = bData.map(item => ({
          id: item.id,
          model: item.name || "New Arrival",
          desc: item.description || "",
          image: item.image ? (item.image.startsWith("http") ? item.image : `${assetBase}/banner/${String(item.image).replace(/^\/+/, "")}`) : "",
        })).filter(i => i.model);
        if (!cancelled) setBanners(normalizedBanners);

        const tPath = backendUrl ? "/testimonials" : "/api/testimonials";
        const tRes = await cachedFetchJson(`${baseUrl}${tPath}?status=active&limit=6`, { ttlMs: 300000 });
        const tData = Array.isArray(tRes) ? tRes : (tRes?.data || []);
        const tImgs = tData.filter(t => t.image).slice(0, 3).map(t => t.image.startsWith("http") ? t.image : `${assetBase}/uploads/${String(t.image).replace(/^\//, "")}`);
        if (!cancelled) {
          setTestimonialImages(tImgs);
          setTestimonialCount(tData.length);
        }

        const rPath = backendUrl ? "/live-videos" : "/api/live-videos";
        const rRes = await cachedFetchJson(`${baseUrl}${rPath}?status=active&limit=12`, { ttlMs: 180000 });
        const rData = Array.isArray(rRes) ? rRes : (rRes?.data || []);
        if (!cancelled) setReels(rData.map(r => ({ id: r.id, url: r.youtube_url || "" })).filter(r => r.url));

      } catch (err) {
        if (!cancelled) setBannerError("Unable to load data");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const targets = { experience: 18, brands: 20, customers: 20000 };
    const timer = setInterval(() => {
      setStats(prev => ({
        experience: Math.min(targets.experience, prev.experience + 1),
        brands: Math.min(targets.brands, prev.brands + 1),
        customers: Math.min(targets.customers, prev.customers + 250),
      }));
    }, 40);
    return () => clearInterval(timer);
  }, []);

  const current = banners[activeLaptop];

  if (isLoading || !current) {
    return <section className="min-h-[40vh] flex items-center justify-center"><p className="text-sm text-gray-500">Loading...</p></section>;
  }

  return (
    <section className="relative overflow-hidden bg-[#f8faff] py-10 sm:py-14 lg:py-18"
      style={{ background: "radial-gradient(900px 520px at 70% 10%, rgba(227,233,255,0.6) 0%, rgba(248,250,255,0.98) 45%, #f8faff 100%)" }}
    >
      <style>{`
        :root {
          --pc-primary: #F36F21;
          --pc-ink: #351F72;
          --pc-blue-1: #e3e9ff;
          --pc-blue-2: #d6e0ff;
          --pc-blue-3: #c6d3ff;
        }
        .hero-fade-in { animation: heroFade 0.8s ease forwards; }
        @keyframes heroFade { 0% { opacity: 0; transform: translateY(14px); } 100% { opacity: 1; transform: translateY(0); } }
        .circle-video { position: relative; width: 100%; height: 100%; overflow: hidden; border-radius: 9999px; background: transparent; cursor: pointer; }
        .circle-video video { transform: translate(-50%, -50%); object-fit: cover; position: absolute; top: 50%; left: 50%; width: 100%; height: 100%; }
        .circle-video iframe { position: absolute; top: 50%; left: 50%; width: 190%; height: 190%; transform: translate(-50%, -50%); transform-origin: center; border: 0; }
        .hero-orbit-track { position: absolute; inset: 0; pointer-events: none; animation: orbitSpin 32s linear infinite; }
        .hero-orbit-node { --angle: 0deg; position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%) rotate(var(--angle)) translateY(calc(-1 * clamp(128px, 22vw, 198px))); }
        .hero-orbit-core { display: flex; height: 40px; width: 40px; align-items: center; justify-content: center; border-radius: 9999px; background: #ffffff; box-shadow: 0 12px 24px rgba(243, 111, 33, 0.24); animation: orbitCounter 32s linear infinite; }
        .hero-orbit-icon { display: flex; height: 30px; width: 30px; align-items: center; justify-content: center; border-radius: 9999px; background: linear-gradient(135deg, #f36f21 0%, #e63946 100%); color: white; }
        .hero-ring-spin { animation: orbitSpin 24s linear infinite; transform-origin: center; }
        .hero-ring-bubble { position: absolute; left: 50%; top: -7px; transform: translateX(-50%); display: flex; height: 14px; width: 14px; align-items: center; justify-content: center; border-radius: 9999px; background: linear-gradient(135deg, #f36f21 0%, #e63946 100%); color: white; box-shadow: 0 8px 16px rgba(243, 111, 33, 0.28); font-size: 8px; }
        .hero-main-shell { position: relative; isolation: isolate; background: radial-gradient(75% 75% at 50% 50%, rgba(255, 255, 255, 0.98) 0%, rgba(243, 247, 255, 0.92) 60%, rgba(228, 236, 255, 0.7) 100%); box-shadow: 0 18px 36px rgba(53, 31, 114, 0.14), 0 10px 22px rgba(243, 111, 33, 0.12); }
        .play-btn-facade { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.05); transition: background 0.3s; z-index: 5; }
        .play-btn-facade:hover { background: rgba(0,0,0,0.15); }
        .play-icon { width: 56px; height: 56px; background: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 10px 25px rgba(0,0,0,0.15); transition: transform 0.3s ease; }
        @media (min-width: 640px) { .play-icon { width: 72px; height: 72px; } }
        .play-btn-facade:hover .play-icon { transform: scale(1.1); }
        @keyframes orbitSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes orbitCounter { 0% { transform: rotate(0deg); } 100% { transform: rotate(-360deg); } }
        .why-card { transition: transform 0.3s ease, box-shadow 0.3s ease; border: 1px solid rgba(31, 60, 136, 0.08); background: #ffffff; }
        .why-card:hover { transform: translateY(-6px); box-shadow: 0 22px 36px rgba(31, 60, 136, 0.18); }
      `}</style>

      <div className="mx-auto w-full max-w-[1320px] px-3 sm:px-4">
        <div className="relative grid items-center gap-10 lg:grid-cols-[1.08fr_0.92fr] lg:gap-14">
          <div className="hero-fade-in group text-left">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#e3e9ff] bg-white/80 px-3.5 py-1.5 text-xs font-semibold text-[#351F72]">
              <span className="h-2 w-2 rounded-full bg-[#F36F21]"></span>
              India’s Trusted Since 2008
            </div>
            <h1 className="mt-4 text-[30px] font-extrabold leading-[1.2] text-[#351F72] sm:text-[42px] lg:text-[50px]">
              𝗣𝗖 𝗖𝗮𝗿𝗲 𝗖𝗼𝗺𝗽𝘂𝘁𝗲𝗿 <br/>
              <span className="text-[var(--pc-primary)]">Trusted Used Laptop Seller</span>
            </h1>
            <p className="mt-3 inline-flex rounded-full border border-[#ffd9c4] bg-[#fff6f1] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[var(--pc-primary)]">
              Any Brand, Any Problem 
            </p>
            <p className="mt-4 max-w-[600px] text-sm font-medium leading-relaxed text-[#4b5876] sm:text-base">
              Since 2008, PC Care has been helping students, professionals, and businesses find reliable laptops at fair prices. Every device is properly checked before sale, so you get performance you can trust.
            </p>
            <div className="mt-6 flex gap-2">
              <button onClick={() => navigate("/products")} aria-label="View our laptop collection" className="rounded-full bg-gradient-to-r from-[#F36F21] to-[#E63946] px-7 py-3 text-lg font-semibold text-white shadow-lg transition hover:-translate-y-1">
                View Laptops
              </button>
              <button onClick={() => navigate("/services")} aria-label="Get technical service support" className="rounded-full border border-[#d5ddf8] bg-white px-6 py-3 text-lg font-semibold text-[#351F72] transition hover:text-[#E63946]">
                Get Service Support
              </button>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-1.5 sm:gap-4">
              {[
                { count: `${stats.experience}+`, label: "Years Experience", icon: "fa-history" },
                { count: `${stats.brands}+`, label: "Laptop Brands", icon: "fa-laptop" },
                { count: `${stats.customers}+`, label: "Customers", icon: "fa-users" }
              ].map((item) => (
                <div key={item.label} className="why-card relative rounded-xl border border-[#d9e4ff] bg-white/90 p-2 sm:p-4 shadow-lg">
                  <div className="inline-flex h-6 w-6 sm:h-9 sm:w-9 items-center justify-center rounded-lg bg-gradient-to-br from-[#F36F21] to-[#E63946] text-white">
                    <i className={`fas ${item.icon} text-xs sm:text-base`}></i>
                  </div>
                  <div className="mt-1.5 text-sm font-extrabold text-[#351F72] sm:mt-3 sm:text-2xl lg:text-[30px]">{item.count}</div>
                  <div className="text-[9px] font-semibold text-[#425176] sm:text-xs lg:text-sm">{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative mt-4 flex justify-center lg:justify-end">
            <div className="hero-main-shell relative z-10 h-[280px] w-[280px] rounded-full sm:h-[410px] sm:w-[410px] lg:h-[500px] lg:w-[500px]">
              <div className="absolute inset-0 rounded-full bg-[var(--pc-blue-1)]"></div>
              <div className="absolute inset-6 rounded-full bg-[var(--pc-blue-2)]"></div>
              <div className="absolute inset-12 rounded-full bg-[var(--pc-blue-3)]"></div>
              
              <div className="absolute inset-[46px] flex items-center justify-center rounded-full bg-white/80 p-[5px] shadow-xl sm:inset-[78px] lg:inset-[96px]">
                <div className="circle-video" onClick={() => setHeroPlaying(true)}>
                  {reels.length > 0 && getVideoId(reels[0].url) ? (
                    <>
                      {heroPlaying ? (
                        <iframe
                          ref={heroIframeRef}
                          onLoad={() => { forceHeroPlay(); setTimeout(forceHeroPlay, 400); }}
                          src={`${EMBED_HOST}/embed/${getVideoId(reels[0].url)}?autoplay=1&mute=1&loop=1&playlist=${getVideoId(reels[0].url)}&controls=0&modestbranding=1&rel=0&playsinline=1&enablejsapi=1&origin=${encodeURIComponent(embedOrigin)}`}
                          title="Hero Video" allow="autoplay; encrypted-media; fullscreen" frameBorder="0"
                        />
                      ) : (
                        <div className="play-btn-facade">
                           <img 
                             src={`https://i.ytimg.com/vi/${getVideoId(reels[0].url)}/hqdefault.jpg`} 
                             alt="Video Thumbnail" 
                             className="absolute inset-0 h-full w-full object-cover"
                           />
                           <div className="play-icon relative z-10">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 5V19L19 12L8 5Z" fill="#F36F21" stroke="#F36F21" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                           </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <video src={heroVideo} autoPlay loop muted playsInline />
                  )}
                </div>
              </div>

              <div className="hero-orbit-track">
                {[
                  { icon: "fa-shield-halved", angle: "0deg" },
                  { icon: "fa-truck-fast", angle: "72deg" },
                  { icon: "fa-face-smile", angle: "144deg" },
                  { icon: "fa-circle-check", angle: "216deg" },
                  { icon: "fa-headset", angle: "288deg" }
                ].map((item, i) => (
                  <div key={i} className="hero-orbit-node" style={{ "--angle": item.angle }}>
                    <div className="hero-orbit-core"><div className="hero-orbit-icon"><i className={`fas ${item.icon} text-[14px]`}></i></div></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="absolute left-[-6%] top-[36%] z-30 hidden w-[158px] rounded-2xl bg-white/90 p-2.5 shadow-xl sm:left-[-10%] sm:block">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#90b6ff] text-white">
                   <i className="fas fa-trophy"></i>
                </div>
                <div>
                  <p className="text-[13px] font-bold text-slate-800">20k+</p>
                  <p className="text-[10px] font-semibold text-slate-600">Happy Customers</p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-[6%] right-[-2%] z-30 hidden w-[158px] rounded-2xl bg-white/90 p-2.5 shadow-xl sm:right-[-4%] sm:block">
              <div className="absolute -top-3 left-4 flex h-7 w-7 items-center justify-center rounded-full bg-[var(--pc-primary)] text-white shadow-lg">
                <i className="fas fa-heart text-[10px]"></i>
              </div>
              <div className="pt-1"><p className="text-[12px] font-semibold text-slate-800">Client Reviews</p></div>
              <div className="mt-1.5 flex items-center justify-between">
                <div className="flex -space-x-2.5">
                  {testimonialImages.map((src, i) => (
                    <img key={i} src={src} alt="Client" className="h-7 w-7 rounded-full border-2 border-white object-cover" />
                  ))}
                </div>
                <div className="text-[11px] font-semibold text-slate-500">{testimonialCount}+</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Herosection;
